import React from 'react';

/**
 * Formats a service name so that any trailing bracketed portion (e.g., "(POS / E-commerce)")
 * stays together on a single line. If the text before the bracket fits but the bracket
 * doesn't, the whole bracket wraps to the next line instead of breaking mid-bracket.
 *
 * Everything is returned inside a single <span> so the result behaves as ONE element.
 * This matters when the caller is a flex container (e.g. icon + label rows): a fragment
 * would turn the label and the bracket into two separate flex items sitting side by side.
 *
 * Example:
 *   "Sales Platform (POS / E-commerce)" renders as:
 *     Sales Platform
 *     (POS / E-commerce)
 *   when the container is too narrow to fit everything on one line.
 */
export function formatServiceName(name: string): React.ReactNode {
  // Capture the trailing (...) group along with any whitespace preceding it,
  // so the original spacing of the name is preserved exactly.
  const match = name.match(/^(.*?)(\s*)(\([^)]+\))$/);

  if (!match) {
    return name;
  }

  const [, prefix, space, bracket] = match;

  return (
    <span className="inline">
      {prefix}
      {/* A real space is already a break opportunity. When the name has no space
          before the bracket (e.g. "Chatbot(Product Inquiry)"), <wbr> adds one
          without inserting a visible gap. */}
      {space ? ' ' : <wbr />}
      <span className="whitespace-nowrap">{bracket}</span>
    </span>
  );
}
